import { useState, useMemo } from 'react';
import { Typography, Tooltip, Box, Button, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

import {
  capitalizeFirstLetter,
  convertAllDates,
  customSort,
  numberWithCommas,
  getInitials,
  downloadExcel,
  downloadPdf,
  normalToCamel,
  numToCurrency,
  getSalesStatusCodes,
} from '../../utils/Functions';
import { IDeal, IDealDeposit, IDealRealtor, IPurchaserInfo, IRealtorInfo } from '../../types/CreateDealForm';
import { IComment } from '../../types/comment';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { Flex, FlexBetween, FlexEnd } from '../../commonStyles';
import { IDocuments } from '../../types/docusign';
import VirtualizedTable from '../tables/VirtualizedTable';
import LoadingWrapper from '../common/LoadingWrapper';
import { GlobalModal } from '../../features/modal/Modal';
import CommentsTable from '../common/CommentsTable';
import { handleModal } from '../../features/modal/modalSlice';
import Dropdown from '../common/formControls/Dropdown';
import { chequeTypes } from '../../utils/Constants';
import StandardTable from '../tables/StandardTable';
import { outstandingChequeTypes } from '../../utils/Constants';

const Outstandings = (props: ChildProps) => {
  const { userType } = props;
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const [activeDeals, setActiveDeals] = useState<IDealComment[]>([]);
  const [allDeals, setAllDeals] = useState<IDealComment[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<any[]>([]);
  const [selectedDeposit, setSelectedDeposit] = useState<any>(null);
  const [type, setType] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(false);
  const [purchaser, setPurchaser] = useState<IPurchaserInfo | null>(null);

  const [sendOutstandings, { loading: outstandingsLoading }] = useMutation(SENDOUTSTANDINGS, {
    onCompleted: (data) => {
      setType('');
      setSelectedDeal([]);
      setSelectedDeposit(null);
      storeDispatch(handleModal(false));
      storeDispatch(showSuccessSnackbar('Outstanding email(s) have been sent'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [callPurchaser, { loading: callLoading }] = useLazyQuery(CALLPURCHASER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(data.callRegistrant));
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const [updateDealDeposit] = useMutation(UPDATEDEPOSIT, {
    onCompleted: (data) => {
      let updatedActiveDeals = activeDeals.map((deal: any) => {
        if (deal._id === selectedDeal[0]._id) {
          let deposit = data.updateDeposits;
          let pendingDeposits = selectedDeal[0].pendingDeposits.map((deposit: any) => {
            if (deposit._id === selectedDeposit._id) {
              return selectedDeposit;
            } else return deposit;
          });
          return {
            ...deal,
            deposit: deposit,
            pendingDeposits: pendingDeposits,
          };
        } else return deal;
      });

      setActiveDeals(updatedActiveDeals);
      setType('');
      storeDispatch(handleModal(false));
      setSelectedDeal([]);
      setSelectedDeposit(null);
      storeDispatch(showSuccessSnackbar('Deposit has been updated!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const { loading } = useQuery(DEALS, {
    skip: userType === 'sales',
    variables: { project: project._id, type: 'all' },
    onCompleted: async (data) => {
      let sortedRows = await customSort(data.getOutstandings, 'unit', 'suite', true);
      setActiveDeals(sortedRows);
      setAllDeals(sortedRows);
    },
    onError: (error) => {
      storeDispatch(showErrorSnackbar(error.toString()));
    },
  });

  const { loading: salesLoading } = useQuery(DEALS, {
    skip: userType !== 'sales',
    variables: { project: project._id, salesRep: user._id },
    onCompleted: async (data) => {
      let sortedRows = await customSort(data.getOutstandings, 'unit', 'suite', true);
      setActiveDeals(sortedRows);
    },
    onError: (error) => {
      storeDispatch(showErrorSnackbar(error.toString()));
    },
  });

  const getStatusDate = (deal: IDeal) => {
    let date;
    switch (deal.unit.status) {
      case 'F':
        date = deal.firmDate;
        break;
      case 'S':
        date = deal.signDate;
        break;
      case 'C':
        date = deal.executeDate;
        break;
      default:
        // Custom sales-category statuses (e.g. brokerage-specific sale codes) are treated as
        // firmed, same as F - Conditional is excluded since it already has its own case above.
        date = getSalesStatusCodes(project).filter((code: string) => code !== 'C').includes(deal.unit.status)
          ? deal.firmDate
          : deal.createdAt;
        break;
    }
    return convertAllDates(date, 'PP');
  };

  const comment = (comment: any) => {
    return (
      <Box>
        <div>{comment ? comment.comment : ''}</div>
        {comment.user.fullName && comment.updatedAt ? (
          <div style={{ marginTop: '10px', fontSize: '9px' }}>
            <em>
              <span style={{ whiteSpace: 'nowrap' }}>{comment ? getInitials(comment.user.fullName) : ''}</span> -{' '}
              <span style={{ whiteSpace: 'nowrap' }}>{comment.updatedAt ? convertAllDates(comment.updatedAt, 'P') : ''}</span>
            </em>
          </div>
        ) : null}
      </Box>
    );
  };

  const getAmendmentBackground = (amendments: IDocuments[]) => {
    if (!amendments.length) return null;
    let allCompleted = true;
    for (const amendment of amendments) {
      if (amendment.status === 'Sent') return outstandingChequeTypes.notReceived;
      if (amendment.status === 'Signed') return outstandingChequeTypes.replacement;
      if (amendment.status !== 'Completed') allCompleted = false;
    }
    return allCompleted ? outstandingChequeTypes.received : outstandingChequeTypes.notReceived;
  };

  const getCheckTypeCell = (deposit: any, pendingDeposits: any[]) => {
    if (deposit.amount === 0) return;
    return (
      <div>
        <div style={{ whiteSpace: 'nowrap' }}>
          Status: {deposit.chequeType ? capitalizeFirstLetter(deposit.chequeType) : <b>Not Received</b>}
        </div>
        <div style={{ whiteSpace: 'nowrap' }}>Amount: ${numberWithCommas(deposit.amount)}</div>
        <div style={{ whiteSpace: 'nowrap' }}>
          {deposit.chequeType && deposit.chequeType !== 'wire' ? 'Cheque Date' : 'Due Date'}:{' '}
          {deposit.chequeType && deposit.chequeType !== 'wire'
            ? deposit.chequeDate
              ? new Date(deposit.chequeDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' })
              : 'Not Received'
            : new Date(deposit.dueDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' })}
        </div>
        {pendingDeposits && pendingDeposits.length ? (
          <div>
            <strong>PENDING DEPOSIT</strong>
          </div>
        ) : null}
      </div>
    );
  };

  const outstandingColumns = useMemo(() => {
    return [
      {
        id: 'selection',
        Header: (
          <Checkbox
            sx={{ color: '#fff' }}
            onClick={() =>
              setSelectedDeal(
                activeDeals.map((deal: IDeal) => {
                  return deal._id;
                })
              )
            }
            checked={selectedDeal.length > 0}
          />
        ),
        accessor: (rowData: IDeal) => {
          return (
            <Box>
              <Checkbox onChange={(e) => handleSelectedDeal(rowData._id!)} checked={selectedDeal.includes(rowData._id?.toString())} />
            </Box>
          );
        },
      },
      {
        Header: 'Suite',
        accessor: (rowData: any) => rowData.unit.suite,
      },
      {
        Header: 'Status',
        accessor: (rowData: any) => rowData.unit.status,
      },
      {
        Header: 'Purchaser',
        accessor: (rowData: any) => rowData.purchasers.map((purchaser: IPurchaserInfo) => purchaser.fullName).join(', '),
      },
      {
        Header: 'Contains Pending Deposits',
        accessor: (rowData: any) => (rowData.pendingDeposits.length ? 'Pending Deposits' : ''),
      },
      {
        Header: 'Realtor',
        accessor: (rowData: any) =>
          rowData.realtor.length ? rowData.realtor.map((realtor: IRealtorInfo) => realtor.fullName).join(', ') : 'Public',
      },
    ];
  }, [selectedDeal]);

  const columns = useMemo(() => {
    const handleRealtorSort = (rowA: any, rowB: any, id: string, desc: any) => {
      if (!rowA.original.realtor.length) {
        return -1;
      }
      if (rowA.original.realtor.length && rowB.original.realtor.length) {
        const a = rowA.original.realtor[0].fullName.toLowerCase();
        const b = rowB.original.realtor[0].fullName.toLowerCase();

        if (a > b) return 1;

        if (b > a) return -1;

        return 0;
      }
    };
    const handlePurchasersSort = (rowA: any, rowB: any, id: string, desc: any) => {
      if (rowA.original.purchasers.length && rowB.original.purchasers.length) {
        const a = rowA.original.purchasers[0].fullName.toLowerCase();
        const b = rowB.original.purchasers[0].fullName.toLowerCase();

        if (a > b) return 1;

        if (b > a) return -1;

        return 0;
      }
    };
    let columns = [
      {
        Header: 'Suite',
        accessor: (rowData: any) => rowData.unit.suite,
        Cell: (props: any) => {
          return (
            <Box sx={{ p: 2 }}>
              <Link
                rel="noopener noreferrer"
                target="_blank"
                style={{ textDecoration: 'none', color: 'inherit' }}
                to={`/${project._id}/dashboard/${props.cell.row.original.unit._id}`}
              >
                <strong style={{ cursor: 'pointer' }}>{props.value}</strong>
              </Link>
            </Box>
          );
        },
      },
      {
        Header: 'Status',
        accessor: (rowData: any) => rowData.unit.status,
        Cell: (props: any) => {
          return <Box sx={{ p: 2 }}>{props.value}</Box>;
        },
      },
      {
        Header: 'Status Date',
        accessor: (rowData: any) => getStatusDate(rowData),
        Cell: (props: any) => {
          return <Box sx={{ p: 2 }}>{props.value}</Box>;
        },
      },
      {
        Header: 'Purchasers',
        width: 450,
        sortType: handlePurchasersSort,
        accessor: (rowData: any) => {
          let purchasers = rowData.purchasers.map((purchaser: IPurchaserInfo, index: number) => {
            return (
              <Box key={index} sx={{ mb: 1 }}>
                <Box>{purchaser.fullName}</Box>
                <Box>{purchaser.email}</Box>
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setPurchaser(purchaser);
                    viewModal(rowData, 'phone');
                  }}
                >
                  <strong>{purchaser.primaryPhone}</strong>
                </Box>
              </Box>
            );
          });
          return <FlexBetween sx={{ flexWrap: 'wrap' }}>{purchasers}</FlexBetween>;
        },
        Cell: (props: any) => {
          return <Box sx={{ p: 2, whiteSpace: 'pre-line' }}>{props.value}</Box>;
        },
      },
      {
        Header: 'Realtor',
        width: '300',
        sortType: handleRealtorSort,
        accessor: (rowData: any) => {
          if (rowData.realtor.length) {
            let realtors = rowData.realtor.map((realtor: IDealRealtor, index: number) => {
              return (
                <Box key={index} sx={{ mb: 1 }}>
                  <Box>{realtor.fullName}</Box>
                  <Box>{realtor.email}</Box>
                  <Box>{realtor.directPhone}</Box>
                </Box>
              );
            });
            return <FlexBetween sx={{ flexWrap: 'wrap' }}>{realtors}</FlexBetween>;
          } else {
            if (rowData.tags.includes('Friends and Family')) {
              return 'Friends and Family';
            } else return 'Public';
          }
        },
        Cell: (props: any) => {
          return <Box sx={{ p: 2, whiteSpace: 'pre-line' }}>{props.value}</Box>;
        },
      },
      {
        Header: 'Mortgage',
        accessor: (rowData: any) => {
          if (
            (rowData.mortgage.amount && rowData.mortgage.getUrl && rowData.mortgage.lender && !rowData.mortgage.revisions.length) ||
            rowData.mortgage.revisions.includes('No MPA Required')
          ) {
            return <CheckIcon />;
          } else {
            let currentDeposit = rowData.deposit;
            if (rowData.pendingDeposits.length) {
              currentDeposit = rowData.pendingDeposits;
            }
            let totalDeposits = currentDeposit.reduce((a: any, b: any) => {
              return a + b.amount;
            }, 0);
            return (
              <Box>
                <Box>{`$${numberWithCommas(rowData.totalPrice - totalDeposits)}`}</Box>
                <Box>
                  {rowData.mortgage.revisions.map((revision: string, index: number) => {
                    return (
                      <Box key={index} sx={{ fontSize: '10px' }}>
                        {revision}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          }
        },
        Cell: (props: any) => {
          let backgroundColor =
            (props.row.original.mortgage.amount &&
              props.row.original.mortgage.getUrl &&
              props.row.original.mortgage.lender &&
              !props.row.original.mortgage.revisions.length) ||
            props.row.original.mortgage.revisions.includes('No MPA Required')
              ? 'rgb(146, 255, 146)'
              : 'rgb(255, 136, 136)';
          return (
            <Box
              sx={{
                backgroundColor: backgroundColor,
                height: '100%',
              }}
            >
              <Box sx={{ textAlign: 'center', position: 'relative', top: '50%', transform: 'translateY(-50%)' }}>{props.value}</Box>
            </Box>
          );
        },
      },
      {
        Header: 'Mortgage Notes',
        width: 400,
        accessor: (rowData: any) => {
          if (rowData.comments.length) {
            let mortgageComment = rowData.comments.find((comment: IComment) => comment.type === 'mortgage' && comment.pinned);
            if (!mortgageComment) {
              mortgageComment = rowData.comments.filter((comment: IComment) => comment.type === 'mortgage');
              if (mortgageComment.length) {
                mortgageComment = mortgageComment.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
              } else {
                mortgageComment = null;
              }
            }

            if (mortgageComment) {
              return (
                <Box sx={{ p: 2, cursor: 'pointer', height: '100%' }} onClick={() => viewModal(rowData, 'mortgage')}>
                  {comment(mortgageComment)}
                </Box>
              );
            } else return <Box onClick={() => viewModal(rowData, 'mortgage')} sx={{ height: '100%', cursor: 'pointer' }}></Box>;
          } else return <Box onClick={() => viewModal(rowData, 'mortgage')} sx={{ height: '100%', cursor: 'pointer' }}></Box>;
        },
      },
      {
        Header: 'Amendments',
        width: 400,
        accessor: (rowData: any) => {
          if (rowData.documents.length) {
            let amendments = rowData.documents.map((document: any, index: number) => {
              return (
                <Box key={index} sx={{ fontSize: '11px' }}>
                  {document.name} - {document.status}
                </Box>
              );
            });
            return <Box>{amendments}</Box>;
          } else return '';
        },
        Cell: (props: any) => {
          let backgroundColor = getAmendmentBackground(props.row.original.documents);
          return (
            <Box
              sx={{
                height: '100%',
                backgroundColor: backgroundColor,
              }}
            >
              <Box sx={{ position: 'relative', p: 2 }}>{props.value}</Box>
            </Box>
          );
        },
      },
      {
        Header: 'Amendment Notes',
        width: 400,
        accessor: (rowData: any) => {
          if (rowData.comments.length) {
            let amendmentComment = rowData.comments.find((comment: IComment) => comment.type === 'amendment' && comment.pinned);
            if (!amendmentComment) {
              amendmentComment = rowData.comments.filter((comment: IComment) => comment.type === 'amendment');
              if (amendmentComment.length) {
                amendmentComment = amendmentComment.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
              } else {
                amendmentComment = null;
              }
            }

            if (amendmentComment) {
              return (
                <Box sx={{ p: 2, cursor: 'pointer', height: '100%' }} onClick={() => viewModal(rowData, 'amendment')}>
                  {comment(amendmentComment)}
                </Box>
              );
            } else return <Box onClick={() => viewModal(rowData, 'amendment')} sx={{ height: '100%', cursor: 'pointer' }}></Box>;
          } else return <Box onClick={() => viewModal(rowData, 'amendment')} sx={{ height: '100%', cursor: 'pointer' }}></Box>;
        },
      },
    ];

    let deposits = activeDeals.map((deal: IDealComment) => deal.deposit.length);
    let largestIndex = Math.max(...deposits);
    let selectedDeal = activeDeals.find((deal: any) => deal.deposit.length === largestIndex);

    let depositColumns: any[] = [];

    if (selectedDeal) {
      let currentDeposit = selectedDeal.deposit;
      if (selectedDeal.pendingDeposits.length) {
        currentDeposit = selectedDeal.pendingDeposits;
      }
      depositColumns = currentDeposit.map((deposit: any, index: number) => {
        return {
          Header: `${deposit.name}`,
          width: 300,
          accessor: (rowData: any) => {
            let rowDeposit = rowData.deposit;
            if (rowData.pendingDeposits.length) {
              rowDeposit = rowData.pendingDeposits;
            }
            if (rowDeposit[index]) {
              return (
                <Box
                  sx={{ height: '100%', cursor: 'pointer', p: 2 }}
                  onClick={() => viewModal(rowData, 'depositChange', rowDeposit[index])}
                >
                  {getCheckTypeCell(rowDeposit[index], rowData.pendingDeposits)}
                </Box>
              );
            } else return '';
          },
          Cell: (props: any) => {
            let rowDeposit = props.row.original.deposit;
            if (props.row.original.pendingDeposits.length) {
              rowDeposit = props.row.original.pendingDeposits;
            }
            let backgroundColor = '#fff';
            if (rowDeposit[index] && rowDeposit[index].amount) {
              backgroundColor = outstandingChequeTypes[rowDeposit[index].chequeType];
              if (!backgroundColor) {
                backgroundColor = '#ff8888';
              }
            }

            return (
              <Box
                sx={{
                  height: '100%',
                  backgroundColor: backgroundColor,
                }}
              >
                <Box sx={{ position: 'relative', p: 2 }}>{props.value}</Box>
              </Box>
            );
          },
        };
      });
    }

    let additionalDeposits = [
      {
        Header: `Additional Deposits`,
        accessor: (rowData: any) => {
          let additional = rowData.additionalDeposits.map((deposit: any, index: number) => {
            return (
              <div key={index}>
                {deposit.name} - {deposit.chequeType ? capitalizeFirstLetter(deposit.chequeType) : 'Not Received'}
              </div>
            );
          });
          return additional;
        },
      },
      {
        Header: 'Deposit Notes',
        width: 400,
        accessor: (rowData: any) => {
          if (rowData.comments.length) {
            let depositComment = rowData.comments.find((comment: IComment) => comment.type === 'deposit' && comment.pinned);
            if (!depositComment) {
              depositComment = rowData.comments.filter((comment: IComment) => comment.type === 'deposit');
              if (depositComment.length) {
                depositComment = depositComment.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
              } else {
                depositComment = null;
              }
            }

            if (depositComment) {
              return (
                <Box sx={{ p: 2, cursor: 'pointer', height: '100%' }} onClick={() => viewModal(rowData, 'deposit')}>
                  {comment(depositComment)}
                </Box>
              );
            } else return <Box onClick={() => viewModal(rowData, 'deposit')} sx={{ height: '100%', cursor: 'pointer' }}></Box>;
          } else return <Box onClick={() => viewModal(rowData, 'deposit')} sx={{ height: '100%', cursor: 'pointer' }}></Box>;
        },
      },
    ];

    return [...columns, ...depositColumns, ...additionalDeposits];
  }, [activeDeals]);

  const tooltip = () => {
    return (
      <Flex>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {Object.keys(outstandingChequeTypes).map((key, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <div
                style={{
                  backgroundColor: outstandingChequeTypes[key],
                  width: '20px',
                  height: '10px',
                  border: '1px solid black',
                  marginRight: '10px',
                }}
              ></div>
              <span>{capitalizeFirstLetter(key)}</span>
            </Box>
          ))}
        </div>
      </Flex>
    );
  };

  const handleCompleted = (outstandings: boolean) => {
    let deals: any = activeDeals.filter((deal: IDeal) => {
      let amendments;
      if (outstandings) {
        amendments = true;
      } else {
        amendments = deal.documents.every((document: any) => document.status === 'Completed');
      }
      let currentDeposit = deal.deposit;
      if (deal.pendingDeposits.length) {
        currentDeposit = deal.pendingDeposits;
      }

      let deposits = currentDeposit.every(
        (deposit: any) =>
          deposit.amount &&
          (deposit.chequeType === 'received' ||
            deposit.chequeType === 'distributed' ||
            deposit.chequeType === 'cleared' ||
            deposit.chequeType === 'wire' ||
            deposit.chequeType === 'floating' ||
            deposit.chequeType === 'pad' ||
            deposit.name === 'Occupancy Deposit' ||
            deposit.name === 'Occupancy')
      );

      return (
        !deposits ||
        !amendments ||
        !deal.mortgage.amount ||
        !deal.mortgage.getUrl ||
        !deal.mortgage.lender ||
        (deal.mortgage.revisions.length !== 0 && !deal.mortgage.revisions.includes('No MPA Required'))
      );
    });
    if (checked && !outstandings) {
      setActiveDeals(allDeals);
    } else {
      setActiveDeals(deals);
    }
    setChecked(!checked);
  };

  const getOutstandings = (deal: IDeal) => {
    let currentDeposit = deal.deposit;
    if (deal.pendingDeposits.length) {
      currentDeposit = deal.pendingDeposits;
    }

    let deposits = currentDeposit.filter(
      (deposit: any) =>
        !deposit.chequeAmount &&
        deposit.chequeType !== 'received' &&
        deposit.chequeType !== 'distributed' &&
        deposit.chequeType !== 'cleared' &&
        deposit.chequeType !== 'wire' &&
        deposit.chequeType !== 'floating' &&
        deposit.chequeType !== 'pad' &&
        deposit.name !== 'Occupancy Deposit' &&
        deposit.name !== 'Occupancy'
    );

    let totalDeposits = currentDeposit.reduce((a: any, b: any) => {
      return a + b.amount;
    }, 0);

    let mortgageAmount;
    if (!deal.mortgage.amount || !deal.mortgage.getUrl || !deal.mortgage.lender) {
      mortgageAmount = `$${numberWithCommas(deal.totalPrice! - totalDeposits)}`;
    }

    return (
      <Box>
        {deposits.map((deposit: IDealDeposit) => {
          return (
            <Box>
              {convertAllDates(deposit.dueDate, 'PP')} - {numToCurrency.format(deposit.amount!)}
            </Box>
          );
        })}
        {mortgageAmount ? <Box>{mortgageAmount}</Box> : null}
      </Box>
    );
  };

  const viewModal = (deal: any, type: string, deposit: any = null) => {
    if (deposit) {
      setSelectedDeposit(deposit);
    }
    if (deal) {
      setSelectedDeal([deal]);
    }
    setType(type);
    storeDispatch(handleModal(true));
  };

  const handleComments = (comments: IComment[]) => {
    let updatedActiveDeals = activeDeals.map((deal: any) => {
      if (deal._id === selectedDeal[0]._id) {
        return {
          ...deal,
          comments: comments,
        };
      } else return deal;
    });
    setActiveDeals(updatedActiveDeals);
    storeDispatch(handleModal(false));
    setSelectedDeal([]);
  };

  const download = (type: string, data: any) => {
    let deposits = activeDeals.map((deal: IDealComment) => deal.deposit.length);
    let largestIndex = Math.max(...deposits);
    let selectedDeal = activeDeals.find((deal: any) => deal.deposit.length === largestIndex);

    let unique = selectedDeal?.deposit.map((deposit: any) => deposit.name);

    let allData = data.map((deal: any) => {
      let totalDeposits = deal.deposit.reduce((a: any, b: any) => {
        return a + b.amount;
      }, 0);

      let mortgageComment = deal.comments.find((comment: IComment) => comment.type === 'mortgage' && comment.pinned);
      if (!mortgageComment) {
        mortgageComment = deal.comments.filter((comment: IComment) => comment.type === 'mortgage');
        if (mortgageComment.length) {
          mortgageComment = mortgageComment.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
        } else {
          mortgageComment = null;
        }
      }

      let amendmentComment = deal.comments.find((comment: IComment) => comment.type === 'amendment' && comment.pinned);
      if (!amendmentComment) {
        amendmentComment = deal.comments.filter((comment: IComment) => comment.type === 'amendment');
        if (amendmentComment.length) {
          amendmentComment = amendmentComment.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
        } else {
          amendmentComment = null;
        }
      }

      let depositComment = deal.comments.find((comment: IComment) => comment.type === 'deposit' && comment.pinned);
      if (!depositComment) {
        depositComment = deal.comments.filter((comment: IComment) => comment.type === 'deposit');
        if (depositComment.length) {
          depositComment = depositComment.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
        } else {
          depositComment = null;
        }
      }

      let depositObjects = unique?.map((unique: string, index: number) => {
        let selectedDeposit = deal.deposit.find((deposit: any) => deposit.name === unique);
        return {
          [normalToCamel(unique)]: selectedDeposit
            ? `${capitalizeFirstLetter(selectedDeposit.chequeType)} - ${numToCurrency.format(selectedDeposit.amount)} ${
                selectedDeposit.chequeDate
                  ? convertAllDates(selectedDeposit.chequeDate, 'PP')
                  : convertAllDates(selectedDeposit.dueDate, 'PP')
              }`
            : '',
        };
      });

      let mergeObjects = depositObjects?.reduce(function (result, currentObject) {
        for (var key in currentObject) {
          if (currentObject.hasOwnProperty(key)) {
            result[key] = currentObject[key];
          }
        }
        return result;
      }, {});

      let outstandingObject = {
        suite: deal.unit.suite,
        status: deal.unit.status,
        statusDate: getStatusDate(deal),
        purchasers: deal.purchasers
          .map((purchaser: any, index: number) => {
            return `${purchaser.fullName} ${purchaser.email} ${purchaser.primaryPhone}\r\n`;
          })
          .join(','),
        realtor: deal.realtor.length
          ? deal.realtor
              .map((realtor: any, index: number) => {
                return `${realtor.fullName} ${realtor.email} ${realtor.directPhone}\r\n`;
              })
              .join(', ')
          : 'N/A',
        mortgage:
          deal.mortgage.amount && deal.mortgage.getUrl && deal.mortgage.lender && !deal.mortgage.revisions.length
            ? 'Complete'
            : `${numToCurrency.format(deal.totalPrice - totalDeposits)}`,
        mortgageRevisions: deal.mortgage.revisions.length
          ? deal.mortgage.revisions
              .map((revision: string) => {
                return `${revision}`;
              })
              .join(', ')
          : '',
        mortgageNotes: mortgageComment ? mortgageComment.comment : '',
        amendments: deal.documents
          .map((document: any, index: number) => {
            return `${document.name} - ${document.status}\r\n`;
          })
          .join(','),
        amendmentNotes: amendmentComment ? amendmentComment.comment : '',
        depositNotes: depositComment ? depositComment.comment : '',
      };

      return { ...outstandingObject, ...mergeObjects };
    });

    let headers = [
      {
        label: 'Suite',
        id: 'suite',
      },
      {
        label: 'Status',
        id: 'status',
      },
      {
        label: 'Status Date',
        id: 'statusDate',
      },
      {
        label: 'Purchasers',
        id: 'purchasers',
      },
      {
        label: 'Realtor',
        id: 'realtor',
      },
      {
        label: 'Mortgage',
        id: 'mortgage',
      },
      {
        label: 'Mortgage Revisions',
        id: 'mortgageRevisions',
      },
      {
        label: 'Mortgage Notes',
        id: 'mortgageNotes',
      },
      {
        label: 'Amendments',
        id: 'amendments',
      },
      {
        label: 'Amendment Notes',
        id: 'amendmentNotes',
      },
      {
        label: 'Deposit Notes',
        id: 'depositNotes',
      },
    ];

    let depositHeaders = unique?.map((deposit: any) => {
      return {
        label: deposit,
        id: normalToCamel(deposit),
      };
    });

    let allHeaders = [...headers, ...depositHeaders!];

    let excelWidths = unique?.map((unique: any) => {
      return {
        [normalToCamel(unique)]: 15,
      };
    });

    let pdfWidths = unique?.map((unique: any) => {
      return {
        [normalToCamel(unique)]: 300,
      };
    });

    let mergeObjects = pdfWidths?.reduce(function (result, currentObject) {
      for (var key in currentObject) {
        if (currentObject.hasOwnProperty(key)) {
          result[key] = currentObject[key];
        }
      }
      return result;
    }, {});

    let mergeExcelObjects = excelWidths?.reduce(function (result, currentObject) {
      for (var key in currentObject) {
        if (currentObject.hasOwnProperty(key)) {
          result[key] = currentObject[key];
        }
      }
      return result;
    }, {});

    let exWidths = {
      suite: 15,
      status: 15,
      statusDate: 15,
      purchasers: 15,
      realtor: 15,
      mortgage: 15,
      mortgageRevisions: 15,
      mortgageNotes: 15,
      amendments: 15,
      amendmentNotes: 15,
      depositNotes: 15,
    };

    let fixedWidths = {
      suite: 200,
      status: 200,
      statusDate: 200,
      purchasers: 200,
      realtor: 200,
      mortgage: 200,
      mortgageRevisions: 200,
      mortgageNotes: 400,
      amendments: 200,
      amendmentNotes: 400,
      depositNotes: 400,
    };

    let allExcelWidths = {
      ...exWidths,
      ...mergeExcelObjects,
    };

    let allPdfWidths = {
      ...fixedWidths,
      ...mergeObjects,
    };

    let sheetTitle = `${project.name} - Outstandings`;

    if (type === 'excel') {
      downloadExcel([allData], [allHeaders], [], [[allExcelWidths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([allData], [allHeaders], [], [allPdfWidths], [sheetTitle], sheetTitle);
    }
  };

  const handleDropdownInput = (e: any) => {
    setSelectedDeposit({
      ...selectedDeposit,
      chequeType: e.target.value,
    });
  };

  const saveDeposit = () => {
    let record: IDealDeposit = {
      chequeType: selectedDeposit.chequeType,
    };

    if (selectedDeposit.chequeType === 'cleared') {
      record.clearedDate = new Date();
    }

    let deposits = selectedDeal[0].deposit;
    if (selectedDeal[0].pendingDeposits && selectedDeal[0].pendingDeposits.length) {
      deposits = selectedDeal[0].pendingDeposits;
    }

    let newDeposits = deposits.map((deposit: IDealDeposit) => {
      if (deposit._id === selectedDeposit._id) {
        return {
          ...deposit,
          chequeType: selectedDeposit.chequeType,
          clearedDate: selectedDeposit.chequeType === 'cleared' ? new Date() : null,
        };
      } else
        return {
          ...deposit,
        };
    });

    updateDealDeposit({
      variables: {
        depositArray: selectedDeal[0].pendingDeposits && selectedDeal[0].pendingDeposits.length ? selectedDeal[0].deposit : newDeposits,
        additionalDepositArray: [],
        pendingDepositArray: selectedDeal[0].pendingDeposits && selectedDeal[0].pendingDeposits.length ? newDeposits : [],
        unitId: selectedDeal[0].unit._id,
        dealId: selectedDeal[0]._id,
      },
    });
  };

  const handleSelectedDeal = (id: string) => {
    if (selectedDeal.includes(id)) {
      let deals = selectedDeal.filter((deal: string) => deal !== id);
      setSelectedDeal(deals);
    } else {
      setSelectedDeal([...selectedDeal, id]);
    }
  };

  const handleCallPurchaser = (purchaser: IPurchaserInfo) => {
    let phone = purchaser.primaryPhone.replace(/\D/g, '');
    if (phone.length < 10 && phone.length > 11) {
      return storeDispatch(showErrorSnackbar('Only US/Canadian Phone Numbers are allowed'));
    }

    callPurchaser({ variables: { project: project._id, customerPhone: parseInt(phone, 10), callType: 'project' } });
  };

  const modalView = () => {
    switch (type) {
      case 'deposit':
      case 'mortgage':
      case 'amendment':
        return (
          <Box>
            <Typography variant="h2" sx={{ mt: 2 }}>
              <strong>
                {selectedDeal[0].unit.suite} - {capitalizeFirstLetter(type)} Notes
              </strong>
            </Typography>
            <CommentsTable
              deal={selectedDeal[0]}
              unit={selectedDeal[0].unit!}
              setComments={handleComments}
              commentType={'deal'}
              comments={selectedDeal[0].comments.filter((comment: IComment) => comment.type === type)}
              commentCategory={type}
            />
          </Box>
        );
      case 'phone':
        return (
          <Box>
            <Typography variant="h2" sx={{ mt: 2 }}>
              <strong>
                {selectedDeal[0].unit.suite} - {purchaser?.fullName}
              </strong>
            </Typography>
            <CommentsTable
              deal={selectedDeal[0]}
              unit={selectedDeal[0].unit!}
              setComments={handleComments}
              commentType={'deal'}
              comments={selectedDeal[0].comments.filter((comment: IComment) => comment.type)}
            />
            <Box>
              <Box sx={{ my: 2 }}>
                <strong>Outstandings List</strong>
              </Box>
              <Box>{getOutstandings(selectedDeal[0])}</Box>
            </Box>
            <FlexEnd sx={{ mt: 2 }}>
              <Button variant="contained" color="success" onClick={() => handleCallPurchaser(purchaser!)}>
                {callLoading ? <CircularProgress size={16} /> : 'Call Purchaser'}
              </Button>
            </FlexEnd>
          </Box>
        );
      case 'depositChange':
        return (
          <Box>
            <Typography variant="h2" component="div" gutterBottom>
              {selectedDeal[0].unit.suite} - {selectedDeposit?.name}
              <Box sx={{ my: 2 }}>
                <Dropdown
                  id={'dropdown'}
                  title={'Type'}
                  menuList={chequeTypes.map((chequeType: any) => chequeType.value.toLowerCase())}
                  name={'chequeInfo'}
                  handleSelect={(e: any) => handleDropdownInput(e)}
                  value={selectedDeposit?.chequeType}
                />
              </Box>
              <Button color="success" variant="contained" onClick={() => saveDeposit()}>
                Update
              </Button>
            </Typography>
          </Box>
        );
      case 'sendOutstandings':
        return outstandingsLoading ? (
          <LoadingWrapper title="Sending Outstanding Notices..." modal={false} />
        ) : (
          <Box>
            <Typography variant="h2" component="div" gutterBottom>
              Send Outstanding Notices
            </Typography>
            <p>Please select the suites below that you would like to send outstanding notices to.</p>
            {selectedDeal.length ? (
              <p>
                Selected: <strong style={{ color: 'green' }}>{selectedDeal.length}</strong>
              </p>
            ) : null}
            <StandardTable columns={outstandingColumns} data={activeDeals} />
            <FlexEnd sx={{ mt: 2 }}>
              <Button
                sx={{ mr: 2 }}
                onClick={() =>
                  setSelectedDeal(activeDeals.filter((deal: IDeal) => deal.unit.status === 'F').map((deal: IDeal) => deal._id))
                }
                color="primary"
                variant="contained"
              >
                Select all Firms
              </Button>
              <Button
                sx={{ mr: 2 }}
                onClick={() => {
                  if (!selectedDeal.length) return storeDispatch(showErrorSnackbar('Please select a deal to preview'));
                  sendOutstandings({ variables: { deals: selectedDeal, preview: true } });
                }}
                color="primary"
                variant="contained"
              >
                Send Preview To Your Email
              </Button>
              <Button
                color="success"
                variant="contained"
                onClick={() => {
                  if (!selectedDeal.length) return storeDispatch(showErrorSnackbar('Please select at least one deal'));
                  sendOutstandings({ variables: { deals: selectedDeal, preview: false } });
                }}
              >
                Send
              </Button>
            </FlexEnd>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <GlobalModal loading={outstandingsLoading}>{modalView()}</GlobalModal>
      <Flex sx={{ mb: 2 }}>
        <Typography sx={{ mr: 1 }} variant={'h5'}>
          <strong>{userType === 'sales' ? `${user.firstName}'s` : ''} Outstandings</strong>
        </Typography>
        <Tooltip title={tooltip()}>
          <InfoIcon sx={{ alignSelf: 'center' }} />
        </Tooltip>
      </Flex>
      {loading ? (
        <LoadingWrapper title={'Outstandings are loading...'} modal={false} />
      ) : (
        <Box>
          <FlexBetween sx={{ mb: 2 }}>
            <Box>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    required={true}
                    checked={checked}
                    onChange={() => handleCompleted(true)}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                }
                label="Show/Hide Completed Files"
                labelPlacement="end"
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleCompleted(true);
                viewModal(null, 'sendOutstandings');
              }}
            >
              Send Outstandings
            </Button>
          </FlexBetween>
          <VirtualizedTable
            itemHeight={150}
            tableHeight={window.innerWidth <= 1280 ? 525 : 750}
            data={activeDeals}
            columns={columns}
            noPadding={true}
            loading={loading || salesLoading}
            hide={true}
            filter={true}
            download={download}
          />
        </Box>
      )}
    </Box>
  );
};

interface ChildProps {
  userType?: string;
}

interface IDealComment extends IDeal {
  comments: IComment[];
}

const CALLPURCHASER = gql`
  query callRegistrant($project: MongoID!, $customerPhone: Float!) {
    callRegistrant(project: $project, customerPhone: $customerPhone)
  }
`;

const UPDATEDEPOSIT = gql`
  mutation updateDeposits(
    $depositArray: [updateCurrentDepositInput!]
    $additionalDepositArray: [updateDepositInput!]
    $pendingDepositArray: [updateDepositInput!]
    $unitId: MongoID!
    $dealId: MongoID!
  ) {
    updateDeposits(
      depositArray: $depositArray
      additionalDepositArray: $additionalDepositArray
      pendingDepositArray: $pendingDepositArray
      unitId: $unitId
      dealId: $dealId
    ) {
      _id
      name
      amount
      dueDate
      accountNumber
      chequeNumber
      chequeDate
      chequeType
      chequeAmount
    }
  }
`;

const SENDOUTSTANDINGS = gql`
  mutation sendOutstandings($deals: [MongoID!], $preview: Boolean!) {
    sendOutstandings(deals: $deals, preview: $preview)
  }
`;

const DEALS = gql`
  query getOutstandings($project: MongoID!, $salesRep: MongoID, $type: String) {
    getOutstandings(project: $project, salesRep: $salesRep, type: $type) {
      _id
      project {
        _id
      }
      unit {
        _id
        status
        suite
        history {
          type
          description
          priceFrom
          priceTo
          timestamp
          user {
            _id
            fullName
          }
        }
      }
      purchasers {
        fullName
        email
        primaryPhone
      }
      documents {
        status
        name
      }
      realtor {
        email
        fullName
        directPhone
      }
      deposit {
        _id
        name
        amount
        dueDate
        accountNumber
        chequeDate
        chequeType
        chequeAmount
        chequeNumber
      }
      pendingDeposits {
        _id
        name
        amount
        dueDate
        accountNumber
        chequeDate
        chequeType
        chequeAmount
        chequeNumber
      }
      options {
        amount
      }
      tags
      mortgage {
        revisions
        lender
        amount
        getUrl
      }
      additionalDeposits {
        name
        chequeType
      }
      comments {
        _id
        type
        pinned
        comment
        updatedAt
        createdAt
        user {
          fullName
        }
      }
      createdAt
      signDate
      executeDate
      firmDate
      basePrice
      totalPrice
    }
  }
`;

export default Outstandings;
